import { updateProfile } from 'firebase/auth'
import getT from 'next-translate/getT'
import getFullName from '@ancon/wildcat-utils/user/getFullName'
import {
  CompanyMemberAccount,
  CompanyMemberAccountCreatePayload,
  CompanyMemberEmailType,
  CompanyMemberInvitation,
  ErrorCode,
} from '@ancon/wildcat-types'

import createAppAsyncThunk from '../../../store/createAppAsyncThunk'
import firebaseAuth from '../../firebase/firebaseAuth'
import getTranslatedError from '../../app/utils/getTranslatedError'
import { clientContextCustomerIdSelector } from '../../clientContext/store/clientContextSelectors'
import api from '../../../api'
import { fetchClientContext } from '../../clientContext/store/clientContextThunks'
import { appLanguageSelector } from '../../app/store/appSelectors'
import localStorageUtils from '../../app/utils/localStorageUtils'
import { linkCredentialsToGuestUser } from '../../auth/store/authThunks'

export const verifyCompanyMemberInvitation = createAppAsyncThunk<
  CompanyMemberInvitation | undefined,
  { token: string }
>(
  'company/verifyMemberInvitation',
  async ({ token }, { getState, rejectWithValue }) => {
    const locale = appLanguageSelector(getState())

    const t = await getT(locale, 'common')

    try {
      const response = await api.user.companies.get.verifyMemberInvitation({
        invitationToken: token,
      })

      return response?.data
    } catch (err) {
      const translatedError = await getTranslatedError(
        err,
        {
          title: t('errors.genericVerifyCompanyInvitationFailure.title'),
          message: t('errors.genericVerifyCompanyInvitationFailure.message'),
        },
        locale,
      )

      return rejectWithValue(translatedError)
    }
  },
)

export const registerMemberWithCompanyInvitation = createAppAsyncThunk<
  CompanyMemberAccount | null,
  {
    companyMemberInvitation: CompanyMemberInvitation
    password: string
  }
>(
  'company/registerMemberWithCompanyInvitation',
  async ({ companyMemberInvitation, password }, { dispatch, getState }) => {
    const locale = appLanguageSelector(getState())

    const customerId = clientContextCustomerIdSelector(getState())

    const { companyMember, company } = companyMemberInvitation

    if (!companyMember || !company) {
      throw new Error('Invalid company member invitation')
    }

    const { id: memberId, email, name, phoneNumber, department } = companyMember
    const { id: companyId } = company

    const memberData: CompanyMemberAccountCreatePayload = {
      firstName: name?.firstName ?? '',
      lastName: name?.lastName ?? '',
      phoneNumber: phoneNumber ?? '',
      department: department ?? null,
      password,
    }

    let firebaseAccountLinked = false

    const isAnonymous = firebaseAuth.currentUser?.isAnonymous

    try {
      if (!customerId || isAnonymous) {
        await dispatch(linkCredentialsToGuestUser({ email: email!, password }))
        firebaseAccountLinked = true

        const response = await api.user.companies.put.createMemberAccount(
          memberData,
          { companyId, memberId },
        )

        // Force refresh the idToken
        await firebaseAuth.currentUser?.getIdToken(true)

        const firebaseUser = firebaseAuth.currentUser
        if (firebaseUser) {
          const updatedProfile = { displayName: getFullName(memberData) }

          // Update firebase user's display name
          await updateProfile(firebaseUser, updatedProfile)
        }

        const companyMemberAccount = response.data.companyMember

        if (companyMemberAccount) {
          // Set the company profile id in local storage
          localStorageUtils.setItem('companyProfileId', companyMemberAccount.id)
        }

        await dispatch(fetchClientContext())

        return companyMemberAccount
      }

      // If the user is already logged in, just create the member account
      const response = await api.user.companies.put.createMemberAccount(
        memberData,
        { companyId, memberId },
      )

      return response.data.companyMember
    } catch (err) {
      const t = await getT(locale, 'common')
      const translatedError = await getTranslatedError(
        err,
        {
          title: t('errors.genericCompanyInvitationSignUpFailure.title'),
          message: t('errors.genericCompanyInvitationSignUpFailure.message'),
        },
        locale,
      )

      if (firebaseAccountLinked) {
        firebaseAuth.currentUser?.delete().catch(error => {
          if (error?.code === ErrorCode.AuthRecentLoginRequiredException) {
            firebaseAuth.signOut()
          }
        })
      }

      const appError = {
        name: translatedError.title,
        message: translatedError.message,
      }

      throw appError as Error
    }
  },
)

export const sendCompanyMemberEmails = createAppAsyncThunk<
  void,
  { companyId: string; memberId: string; emailType: CompanyMemberEmailType }
>(
  'company/sendCompanyMemberEmails',
  async ({ companyId, memberId, emailType }, { getState, rejectWithValue }) => {
    const locale = appLanguageSelector(getState())
    const t = await getT(locale, 'common')
    try {
      await api.user.companies.post.sendMemberEmails(
        { emailType },
        { companyId, memberId },
      )
      return undefined
    } catch (err) {
      const translatedError = await getTranslatedError(
        err,
        {
          title: t('errors.genericCompanyMemberEmailSendFailure.title'),
          message: t('errors.genericCompanyMemberEmailSendFailure.message'),
        },
        locale,
      )

      return rejectWithValue(translatedError)
    }
  },
)
